import { generateBadge, type BadgeSpec } from '@fp/badges';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { get, qs } from '../lib/api';
import { Badge, BadgeEditor } from './badges';
import { Field } from './ui';

export interface NewTeam {
  name: string;
  shortName: string;
  country: string;
  badge: BadgeSpec;
}
export interface NewCompetition {
  name: string;
  shortName: string;
  isCup: boolean;
  badge: BadgeSpec;
}

export type Picked<C> = { kind: 'existing'; id: number; name: string; badge: BadgeSpec; detail: string | null } | { kind: 'new'; create: C } | null;

interface Suggestion {
  id: number;
  name: string;
  detail: string | null;
  badge: BadgeSpec;
}

function useDebounced<T>(value: T, ms = 180): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Combobox over existing teams or competitions. If nothing matches, the last
 * option creates a new one: it is created together with the match, with a
 * generated badge the admin can recolour or shuffle right here.
 */
export function EntityPicker<K extends 'team' | 'competition'>({
  kind,
  label,
  value,
  onChange,
  season,
  exclude,
}: {
  kind: K;
  label: string;
  value: Picked<K extends 'team' ? NewTeam : NewCompetition>;
  onChange: (v: Picked<K extends 'team' ? NewTeam : NewCompetition>) => void;
  season?: string;
  exclude?: number;
}) {
  type C = K extends 'team' ? NewTeam : NewCompetition;
  const id = useId();
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const q = useDebounced(text.trim());
  const inputRef = useRef<HTMLInputElement>(null);
  const { data } = useQuery({
    queryKey: ['suggest', kind, q, season],
    queryFn: () => get<{ results: Suggestion[] }>(`/admin/suggest${qs({ kind, q, season })}`),
    enabled: q.length > 0,
  });
  const results = (data?.results ?? []).filter((r) => r.id !== exclude);
  const exact = results.some((r) => r.name.toLowerCase() === text.trim().toLowerCase());
  const options: ({ type: 'existing'; s: Suggestion } | { type: 'create'; name: string })[] = useMemo(
    () => [
      ...results.map((s) => ({ type: 'existing' as const, s })),
      ...(text.trim() && !exact ? [{ type: 'create' as const, name: text.trim().replace(/\s+/g, ' ') }] : []),
    ],
    [results, text, exact],
  );

  const choose = (i: number) => {
    const o = options[i];
    if (!o) return;
    if (o.type === 'existing') onChange({ kind: 'existing', id: o.s.id, name: o.s.name, badge: o.s.badge, detail: o.s.detail });
    else {
      const badge = generateBadge({ kind, name: o.name });
      const create = (kind === 'team' ? { name: o.name, shortName: '', country: '', badge } : { name: o.name, shortName: '', isCup: false, badge }) as C;
      onChange({ kind: 'new', create });
    }
    setText('');
    setOpen(false);
  };

  if (value) {
    const name = value.kind === 'existing' ? value.name : value.create.name;
    const badge = value.kind === 'existing' ? value.badge : value.create.badge;
    return (
      <div className="field">
        <span>{label}</span>
        <div className="chosen">
          <Badge spec={badge} size={26} />
          <span className="grow" style={{ fontWeight: 600 }}>
            {name}
            {value.kind === 'existing' && value.detail && <span className="muted small"> · {value.detail}</span>}
          </span>
          {value.kind === 'new' && <span className="pill pill-green">New</span>}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              onChange(null);
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
          >
            Change
          </button>
        </div>
        {value.kind === 'new' && (
          <div className="new-box">
            <span className="small" style={{ fontWeight: 400 }}>
              {kind === 'team' ? 'This team' : 'This competition'} doesn't exist yet. It will be created with the match.
            </span>
            <div className="form-grid">
              <Field label="Name">
                <input className="input" value={value.create.name} maxLength={60} onChange={(e) => onChange({ kind: 'new', create: { ...value.create, name: e.target.value } })} />
              </Field>
              <Field label="Short name" hint="Optional, up to 12 characters">
                <input
                  className="input"
                  value={value.create.shortName}
                  maxLength={12}
                  onChange={(e) => {
                    const shortName = e.target.value;
                    const text = /^[A-Za-z0-9]{1,3}$/.test(shortName) ? shortName.toUpperCase() : value.create.badge.text;
                    onChange({ kind: 'new', create: { ...value.create, shortName, badge: { ...value.create.badge, text } } });
                  }}
                />
              </Field>
              {'country' in value.create && (
                <Field label="Country" hint="Optional">
                  <input className="input" value={value.create.country} maxLength={56} onChange={(e) => onChange({ kind: 'new', create: { ...value.create, country: e.target.value } as C })} />
                </Field>
              )}
              {'isCup' in value.create && (
                <label className="check" style={{ alignSelf: 'end' }}>
                  <input type="checkbox" checked={value.create.isCup} onChange={(e) => onChange({ kind: 'new', create: { ...value.create, isCup: e.target.checked } as C })} />
                  Knockout cup
                </label>
              )}
            </div>
            <BadgeEditor value={value.create.badge} name={value.create.name} onChange={(badge) => onChange({ kind: 'new', create: { ...value.create, badge } })} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="field combo">
      <label htmlFor={id}>{label}</label>
      <input
        ref={inputRef}
        id={id}
        className="input"
        role="combobox"
        aria-expanded={open && options.length > 0}
        aria-controls={`${id}-list`}
        aria-activedescendant={open && options[active] ? `${id}-o${active}` : undefined}
        autoComplete="off"
        placeholder={kind === 'team' ? 'Type a team name' : 'Type a competition name'}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setActive(0);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, options.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            choose(active);
          } else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && options.length > 0 && (
        <ul className="combo-list" id={`${id}-list`} role="listbox">
          {options.map((o, i) => (
            <li
              key={o.type === 'existing' ? o.s.id : 'create'}
              id={`${id}-o${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                choose(i);
              }}
              onMouseEnter={() => setActive(i)}
            >
              {o.type === 'existing' ? (
                <>
                  <Badge spec={o.s.badge} size={22} />
                  <span className="grow">{o.s.name}</span>
                  {o.s.detail && <span className="muted small">{o.s.detail}</span>}
                </>
              ) : (
                <span className="create">+ Create “{o.name}”</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The request body piece for a picked value. */
export function refOf(v: Picked<NewTeam> | Picked<NewCompetition>) {
  if (!v) return null;
  if (v.kind === 'existing') return { id: v.id };
  const c = v.create;
  return {
    create: {
      name: c.name,
      shortName: c.shortName || null,
      ...('country' in c ? { country: c.country || null } : {}),
      ...('isCup' in c ? { isCup: c.isCup } : {}),
      badge: c.badge,
    },
  };
}
